import { FormArray, FormBuilder, FormControl, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { AbstractDescripteursForm } from '@app/shared/form-models/abstract-descripteurs-form';
import {
    BacASable,
    Espace,
    FormatEnseignement,
    FormatsEnseignement,
    Periode,
    TypeEspace,
    TypeObjetMaquette
} from '@pcscol/odf-api-v1-angular';
import { descripteursFormatsEnseignementValidators } from '@app/shared/base-form/validators-config';
import { VolumeHoraire } from './volume-horaire-input/volume-horaire-input.component';
import { NomenclatureType } from '@app/core/models/nomenclature.model';

export class DescripteursFormatEnseignementsForm extends AbstractDescripteursForm {
    constructor(
        protected formGroup: FormGroup,
        protected typeObjetMaquette: TypeObjetMaquette,
        private espace: Espace,
        private fb: FormBuilder | NonNullableFormBuilder
    ) {
        super(formGroup, typeObjetMaquette);
    }

    public static createForm(
        fb: FormBuilder | NonNullableFormBuilder,
        typeObjetMaquette: TypeObjetMaquette,
        espace: Espace,
        descripteursFormatsEnseignements?: FormatsEnseignement
    ) {
        const formGroup = fb.group({
            structuresPorteuse: fb.array<string>([], { validators: descripteursFormatsEnseignementValidators.structuresPorteuse }),
            formationPorteuse: fb.control<string | null>(null, { validators: descripteursFormatsEnseignementValidators.formationPorteuse }),
            formatsEnseignement: fb.array<FormatEnseignement>([], {
                validators: descripteursFormatsEnseignementValidators.formatsEnseignement
            })
        });

        if (descripteursFormatsEnseignements) {
            formGroup.patchValue(descripteursFormatsEnseignements);
            descripteursFormatsEnseignements.structuresPorteuse?.forEach((st: string) => {
                (formGroup.controls.structuresPorteuse as FormArray).push(fb.control<string | null>(st));
            });
            descripteursFormatsEnseignements.formatsEnseignement?.forEach((f: FormatEnseignement) => {
                (formGroup.controls.formatsEnseignement as FormArray).push(
                    fb.group({
                        id: fb.control<string | null>(f.id ?? null),
                        version: fb.control<number | null>(f.version ?? null),
                        modalite: fb.control<string | null>(f.modalite?.code ?? null, {
                            validators: descripteursFormatsEnseignementValidators.modalite
                        }),
                        typeHeure: fb.control<string | null>(f.typeHeure?.code ?? null, {
                            validators: descripteursFormatsEnseignementValidators.typeHeure
                        }),
                        volumeHoraire: fb.control<VolumeHoraire | null>(
                            f.volumeHoraire ? VolumeHoraire.fromMinutes(f.volumeHoraire) : null,
                            {
                                validators: descripteursFormatsEnseignementValidators.volumeHoraire
                            }
                        ),
                        nombreTheoriqueDeGroupes: fb.control<number | null>(f.nombreTheoriqueDeGroupes ?? null, {
                            validators: descripteursFormatsEnseignementValidators.nombreTheoriqueDeGroupes
                        }),
                        seuilDedoublement: fb.control<number | null>(f.seuilDedoublement ?? null, {
                            validators: descripteursFormatsEnseignementValidators.seuilDedoublement
                        })
                    })
                );
            });
            formGroup.updateValueAndValidity();
        }
        if (typeObjetMaquette === TypeObjetMaquette.Formation) {
            (<FormGroup>formGroup).removeControl('formationPorteuse');
        }
        return new DescripteursFormatEnseignementsForm(formGroup, typeObjetMaquette, espace, fb);
    }

    get asFormatEnseignementsRequest() {
        return {
            ...this.formGroup.getRawValue(),
            formatsEnseignement: [
                ...this.formGroup.controls.formatsEnseignement.value.map(e => {
                    const res = {
                        ...e,
                        id: e.id ?? null,
                        version: e.version ?? null,
                        modalite: e.modalite
                            ? {
                                  type: NomenclatureType.ModaliteEnseignement,
                                  code: e.modalite,
                                  dateConsommation: this.dateConsommation
                              }
                            : null,
                        typeHeure: e.typeHeure
                            ? {
                                  type: NomenclatureType.TypeHeure,
                                  code: e.typeHeure,
                                  dateConsommation: this.dateConsommation
                              }
                            : null,
                        volumeHoraire: e.volumeHoraire ? e.volumeHoraire.toMinutes() : null
                    };
                    return res;
                })
            ]
        };
    }

    get formatsEnseignementsArray() {
        return this.formGroup.controls.formatsEnseignement as FormArray;
    }

    getFormArrayAtIndex(index: number): FormGroup {
        return this.formatsEnseignementsArray.at(index) as FormGroup;
    }

    getVolumeHoraireAtIndex(index: number) {
        return (this.formatsEnseignementsArray.at(index) as FormGroup).controls.volumeHoraire as FormControl;
    }

    private get dateConsommation(): string {
        if (this.espace.typeEspace === TypeEspace.Periode) {
            return (this.espace as Periode).validite.dateDebut;
        } else {
            return (this.espace as BacASable).dateConsommationNomenclatures;
        }
    }

    addFormatEnseignement() {
        this.formatsEnseignementsArray.push(
            this.fb.group({
                modalite: this.fb.control<string | null>(null, { validators: descripteursFormatsEnseignementValidators.modalite }),
                typeHeure: this.fb.control<string | null>(null, { validators: descripteursFormatsEnseignementValidators.typeHeure }),
                volumeHoraire: this.fb.control<VolumeHoraire | null>(null, {
                    validators: descripteursFormatsEnseignementValidators.volumeHoraire
                }),
                nombreTheoriqueDeGroupes: this.fb.control<number | null>(null, {
                    validators: descripteursFormatsEnseignementValidators.nombreTheoriqueDeGroupes
                }),
                seuilDedoublement: this.fb.control<number | null>(null, {
                    validators: descripteursFormatsEnseignementValidators.seuilDedoublement
                })
            })
        );
    }

    resetFormationPorteuse() {
        this.formGroup.controls.formationPorteuse.reset();
        this.formGroup.controls.formationPorteuse.markAsDirty();
    }
}
